import { ApiError } from './apiClient';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  duration?: number;
  persistent?: boolean;
  actions?: NotificationAction[];
}

export interface NotificationAction {
  label: string;
  action: () => void;
  variant?: 'text' | 'outlined' | 'contained';
}

class NotificationService {
  private listeners: ((notification: Notification) => void)[] = [];
  private removalListeners: ((id: string) => void)[] = [];
  private notifications: Notification[] = [];

  subscribe(listener: (notification: Notification) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  subscribeRemovals(listener: (id: string) => void) {
    this.removalListeners.push(listener);
    return () => {
      this.removalListeners = this.removalListeners.filter(l => l !== listener);
    };
  }

  private notify(notification: Omit<Notification, 'id'>) {
    const fullNotification: Notification = {
      ...notification,
      id: `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };

    this.notifications.push(fullNotification);
    this.listeners.forEach(listener => listener(fullNotification));

    // Auto-remove non-persistent notifications
    if (!fullNotification.persistent) {
      setTimeout(() => {
        this.remove(fullNotification.id);
      }, fullNotification.duration || 5000);
    }

    return fullNotification.id;
  }

  success(title: string, message: string, options?: Partial<Notification>) {
    return this.notify({
      type: 'success',
      title,
      message,
      duration: 4000,
      ...options,
    });
  }

  error(title: string, message: string, options?: Partial<Notification>) {
    return this.notify({
      type: 'error',
      title,
      message,
      duration: 8000,
      ...options,
    });
  }

  warning(title: string, message: string, options?: Partial<Notification>) {
    return this.notify({
      type: 'warning',
      title,
      message,
      duration: 6000,
      ...options,
    });
  }

  info(title: string, message: string, options?: Partial<Notification>) {
    return this.notify({
      type: 'info',
      title,
      message,
      duration: 5000,
      ...options,
    });
  }

  // Handle API errors with user-friendly messages
  handleApiError(error: ApiError, context?: string) {
    let title = 'Error';
    let message = error.message;

    // Customize messages based on status code
    switch (error.statusCode) {
      case 0:
        title = 'Connection Error';
        message = 'Unable to connect to the server. Please check your internet connection.';
        break;
      case 400:
        title = 'Invalid Request';
        message = error.details || 'Please check your input and try again.';
        break;
      case 401:
        title = 'Authentication Required';
        message = 'Please log in to continue.';
        break;
      case 403:
        title = 'Access Denied';
        message = 'You do not have permission to perform this action.';
        break;
      case 404:
        title = 'Not Found';
        message = 'The requested resource could not be found.';
        break;
      case 409:
        // The server refused for a reason it states in fixed wording (a job already running, a
        // rubric without a CAD category); that reason is the message.
        title = 'Not Possible Right Now';
        message = error.message || 'The request conflicts with the current state. Try again.';
        break;
      case 408:
        title = 'Request Timeout';
        message = 'The request took too long. Please try again.';
        break;
      case 429:
        title = 'Too Many Requests';
        message = 'Please wait a moment before trying again.';
        break;
      case 500:
        title = 'Server Error';
        message = 'An internal server error occurred. Please try again later.';
        break;
      case 502:
      case 503:
      case 504:
        title = 'Service Unavailable';
        message = 'The service is temporarily unavailable. Please try again later.';
        break;
      default:
        title = 'Unexpected Error';
        message = error.message || 'An unexpected error occurred.';
    }

    // Add context if provided
    if (context) {
      title = `${context}: ${title}`;
    }

    return this.error(title, message, {
      persistent: error.statusCode >= 500, // Keep server errors visible longer
      actions: error.statusCode >= 500 ? [
        {
          label: 'Retry',
          action: () => window.location.reload(),
          variant: 'outlined',
        },
      ] : undefined,
    });
  }

  // Handle processing errors with retry options
  handleProcessingError(operation: string, error: unknown, retryAction?: () => void) {
    const actions: NotificationAction[] = [];
    
    if (retryAction) {
      actions.push({
        label: 'Retry',
        action: retryAction,
        variant: 'contained',
      });
    }

    return this.error(
      `${operation} Failed`,
      (error as Error).message || 'An error occurred during processing.',
      {
        persistent: true,
        actions,
      }
    );
  }

  remove(id: string) {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.removalListeners.forEach(listener => listener(id));
  }

  clear() {
    this.notifications = [];
  }

  getNotifications() {
    return [...this.notifications];
  }
}

export const notificationService = new NotificationService();